import React, { useState } from 'react';
import { ChevronDown, Sparkles, Image as ImageIcon, Film } from 'lucide-react';
import type { GenerationParams, GenerationOptions } from '@/types/agent';

const IMAGE_MODELS = [
  { id: 'grsai-gpt-image-2', name: 'GPT Image 2', backend: 'grsai' },
  { id: 'grsai-nano-banana-pro', name: 'Nano Banana Pro', backend: 'grsai' },
  { id: 'gemini-2.5-flash-image', name: 'Gemini 2.5 Flash', backend: 'gemini' },
  { id: 'gemini-3-pro-image-preview', name: 'Gemini 3 Pro', backend: 'gemini' },
  { id: 'agnes-image-2.1-flash', name: 'Agnes Image 2.1 Flash', backend: 'agnes' },
];

const VIDEO_MODELS = [
  { id: 'grsai-sora-2', name: 'Sora 2', backend: 'grsai' },
  { id: 'veo-3.1-fast-generate-preview', name: 'Veo 3.1 Fast', backend: 'gemini' },
  { id: 'fal-ai-kling-video-v3-standard-text-to-video', name: 'Kling V3', backend: 'falai' },
  { id: 'agnes-video-v2.0', name: 'Agnes Video V2.0', backend: 'agnes' },
  { id: 'agnes-video-v2.0-img2video', name: 'Agnes Video V2.0 (图生视频)', backend: 'agnes' },
];

const ASPECT_RATIOS = [
  { value: '1:1', label: '1:1' },
  { value: '16:9', label: '16:9' },
  { value: '9:16', label: '9:16' },
  { value: '4:3', label: '4:3' },
  { value: '3:4', label: '3:4' },
  { value: '3:2', label: '2:3' },
  { value: '2:3', label: '3:2' },
];

const RESOLUTIONS = [
  { value: '1K', label: '1K' },
  { value: '2K', label: '2K' },
  { value: '4K', label: '4K' },
];

const IMAGE_COUNTS = [1, 2, 3, 4];

interface GenerationConfirmPanelProps {
  options: GenerationOptions;
  onConfirm: (params: GenerationParams) => void;
  onCancel: () => void;
}

export const GenerationConfirmPanel: React.FC<GenerationConfirmPanelProps> = ({
  options,
  onConfirm,
  onCancel,
}) => {
  const [params, setParams] = useState<GenerationParams>({
    model: options.defaultParams?.model || options.availableModels?.[0]?.id || '',
    aspectRatio: options.defaultParams?.aspectRatio || '1:1',
    resolution: options.defaultParams?.resolution,
    imageCount: options.defaultParams?.imageCount || 1,
    duration: options.defaultParams?.duration,
  });

  const models = options.availableModels.length > 0
    ? options.availableModels
    : options.taskType === 'image' ? IMAGE_MODELS : VIDEO_MODELS;

  const currentModel = models.find(m => m.id === params.model) || models[0];
  const showResolution = true;
  const showImageCount = options.taskType === 'image';

  const aspectRatios = options.availableAspectRatios.length > 0
    ? ASPECT_RATIOS.filter(ar => options.availableAspectRatios.includes(ar.value))
    : ASPECT_RATIOS;

  const resolutions = options.availableResolutions.length > 0
    ? RESOLUTIONS.filter(r => options.availableResolutions.includes(r.value))
    : RESOLUTIONS;

  const isImg2Img = options.mode === 'img2img' || options.mode === 'img2video';

  return (
    <div className="rounded-lg border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent overflow-hidden">
      <div className="px-3 py-2.5 border-b border-white/5 flex items-center gap-2">
        {options.taskType === 'image' ? (
          <ImageIcon size={14} className="text-cyan-400" />
        ) : (
          <Film size={14} className="text-purple-400" />
        )}
        <span className="text-[12px] font-medium text-white">
          {options.taskType === 'image' ? '图片生成' : '视频生成'}
        </span>
        {isImg2Img && (
          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400 border border-cyan-500/20">
            图生{options.taskType === 'image' ? '图' : '视频'}
          </span>
        )}
      </div>

      <div className="px-3 py-2.5 space-y-3">
        <div className="text-[11px] text-slate-300">
          <span className="text-slate-500">提示词：</span>
          {options.prompt}
        </div>

        {isImg2Img && (
          <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-cyan-500/10 border border-cyan-500/20">
            <span className="text-[10px] text-cyan-400">
              📷 检测到 {options.referenceImageCount} 张参考图片，将保持人物一致性
            </span>
          </div>
        )}

        <div className="space-y-2.5">
          <div className="space-y-1">
            <label className="text-[10px] text-slate-500 uppercase tracking-wider">模型</label>
            <div className="relative">
              <select
                value={params.model || models[0].id}
                onChange={e => setParams({ ...params, model: e.target.value })}
                className="w-full appearance-none bg-white/5 border border-white/10 rounded-md px-2.5 py-1.5 text-[11px] text-white focus:outline-none focus:border-cyan-500/50 transition-colors pr-7"
              >
                {models.map(m => (
                  <option key={m.id} value={m.id} className="bg-[#1e1e2e] text-white">
                    {m.name}
                  </option>
                ))}
              </select>
              <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] text-slate-500 uppercase tracking-wider">尺寸</label>
            <div className="flex flex-wrap gap-1">
              {aspectRatios.map(ar => (
                <button
                  key={ar.value}
                  onClick={() => setParams({ ...params, aspectRatio: ar.value })}
                  className={`px-2 py-1 rounded text-[10px] transition-colors ${
                    (params.aspectRatio || '1:1') === ar.value
                      ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                      : 'bg-white/5 text-slate-400 border border-white/10 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {ar.label}
                </button>
              ))}
            </div>
          </div>

          {showResolution && resolutions.length > 0 && (
            <div className="space-y-1">
              <label className="text-[10px] text-slate-500 uppercase tracking-wider">分辨率</label>
              <div className="flex gap-1">
                {resolutions.map(r => (
                  <button
                    key={r.value}
                    onClick={() => setParams({ ...params, resolution: r.value })}
                    className={`px-2.5 py-1 rounded text-[10px] transition-colors ${
                      params.resolution === r.value
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                        : 'bg-white/5 text-slate-400 border border-white/10 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {showImageCount && (
            <div className="space-y-1">
              <label className="text-[10px] text-slate-500 uppercase tracking-wider">生成数量</label>
              <div className="flex gap-1">
                {IMAGE_COUNTS.map(n => (
                  <button
                    key={n}
                    onClick={() => setParams({ ...params, imageCount: n })}
                    className={`w-7 h-7 rounded text-[10px] transition-colors ${
                      (params.imageCount || 1) === n
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                        : 'bg-white/5 text-slate-400 border border-white/10 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          <button
            onClick={() => onConfirm(params)}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-[12px] font-medium hover:from-cyan-400 hover:to-blue-400 transition-all hover:shadow-lg hover:shadow-cyan-500/20"
          >
            <Sparkles size={13} />
            确认生成
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-400 text-[12px] hover:bg-white/10 hover:text-white transition-colors"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
};
